// --------------------------------------------------------------
// Created On: 2024-05-28
// Author: Hannah Vaughan
//
// Last Modified: 2024-10-23
// Modified By: Jonathon Hicke
//
// Copyright 2024 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useMemo, useState } from "react";
import useApi from "../../hooks/useApi";
import Error500Page from "../Error500Page/Error500Page";
import Spinner from "../../components/Spinner/Spinner";
import apiRequest from "../../utilities/api/apiRequest";
import CompanyListItem from "./CompanyListItem/CompanyListItem";
import styles from "./CompanyDistributorPage.module.scss";
import deepCopy from "../../utilities/deepCopy";
import getApiError from "../../utilities/api/getApiError";
import { API } from "../../constants/miscellaneous";

// Page that displays all companies and their linked distributor accounts if they have one.
// This page also allows users to add and delete links between companies and distributors.
export default function CompanyDistributorPage(): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [failedToLoad, setFailedToLoad] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [companies, setCompanies] = useState<Company[]>([]);
  const [distributors, setDistributors] = useState<DistributorAccount[]>([]);
  const [companyFilter, setCompanyFilter] = useState<string>("");
  const [isValidDistributorName, setIsValidDistributorName] = useState<boolean>(false);
  const [openCompanyId, setOpenCompanyId] = useState<number | null>(null);

  const filteredCompanies = useMemo(() => {
    if (companyFilter.length > 0) {
      const matchedCompanies = companies.filter(
        (company) => company.name.toLowerCase().indexOf(companyFilter.toLowerCase()) >= 0
      );
      return matchedCompanies;
    } else {
      return companies;
    }
  }, [JSON.stringify(companies), companyFilter]);

  // Gets company and distributor data from the API.
  useApi(
    () => {
      setLoading(true);
      return true;
    },
    {
      url: `${API}/manage-company-distributors`,
      method: "GET",
    },
    async (response: Response, responseBody: ResponseBody) => {
      if (response.ok && responseBody) {
        setCompanies(responseBody.companies);
        setDistributors(responseBody.distributors);
        setFailedToLoad(false);
      } else {
        setFailedToLoad(true);
      }
      setLoading(false);
    },
    []
  );

  // Updates the record of which company is open when a new one is clicked, then clears the errorMessage and
  // isValidDistributorName state variables.
  function toggleOpenCompany(companyId: number | null): void {
    setOpenCompanyId(companyId);
    setErrorMessage("");
    setIsValidDistributorName(false);
  }

  // Deletes the link between the company with the provided companyId and its current distributor.
  async function deleteCompanyDistributorLink(companyId: number): Promise<void> {
    setLoading(true);
    const [response] = (await apiRequest(`${API}/manage-company-distributors/${companyId}`, "DELETE", null)) as [
      Response,
      DistributorLinkResponseBody
    ];
    setLoading(false);

    if (response.ok) {
      setErrorMessage("");

      // Update the company distributor link in the component's state.
      const currentCompanies = deepCopy(companies);
      for (const currentCompany of currentCompanies) {
        if (currentCompany.companyId === companyId) {
          currentCompany.distributorAccountId = null;
          break;
        }
      }
      setCompanies(currentCompanies);
    } else {
      setErrorMessage(await getApiError(response, "Unable to delete link between company and distributor."));
    }
    setIsValidDistributorName(false);
  }

  // Checks if the value entered into the distributor search field can be connected to an existing distributor that is
  // not already linked to a company. If the value matches a valid and available distributor account, the add link
  // will become accessible.
  function checkCompanyDistributorLink(filter: string): void {
    // Find the distributor with the matching name, if one exists.
    const matchedDistributor = distributors.find(
      (distributor) => distributor.name.toLowerCase() === filter.toLowerCase()
    );

    // Validate that the distributor is not already associated with another company, if it exists.
    if (matchedDistributor !== undefined) {
      // Search for a company that has a distributorAccountId equal to the one found from the filter.
      const matchedCompany = companies.find(
        (company) =>
          company.distributorAccountId !== null &&
          company.distributorAccountId === matchedDistributor.distributorAccountId
      );

      // Set an error message if another company is found to already be linked to the selected distributor.
      if (matchedCompany !== undefined) {
        setErrorMessage(`This account is already associated with the company ${matchedCompany.name}.`);
        setIsValidDistributorName(false);
      } else {
        // The filter's distributor name is only considered valid if it exists in the distributor list but is not
        // linked to any company.
        setErrorMessage("");
        setIsValidDistributorName(true);
      }
    } else {
      // An error message is not necessary, but the value is not currently associated with any existing distributors.
      setErrorMessage("");
      setIsValidDistributorName(false);
    }
  }

  // Links the distributor account with the provided distributorAccountId to the company with the provided companyId.
  // It can be assumed that the value in the distributor search field is connected with an existing distributor that is
  // not currently linked to another company.
  // The button that triggers this function is disabled unless the distributor has already been verified.
  async function linkDistributorAccount(companyId: number, distributorAccountId: number): Promise<void> {
    setLoading(true);
    const [response] = (await apiRequest(`${API}/manage-company-distributors/${companyId}`, "PUT", {
      distributorAccountId: distributorAccountId,
    })) as [Response, DistributorLinkResponseBody];
    setLoading(false);

    // Update the errorMessage and isValidateDistributorName state variables depending on the response.
    if (response.ok) {
      setErrorMessage("");

      // Update the company distributor link in the component's state.
      const currentCompanies = deepCopy(companies);
      for (const currentCompany of currentCompanies) {
        if (currentCompany.companyId === companyId) {
          currentCompany.distributorAccountId = distributorAccountId;
          break;
        }
      }
      setCompanies(currentCompanies);
    } else {
      setErrorMessage(await getApiError(response, "Unable to link the requested distributor to the company."));
    }
    setIsValidDistributorName(false);
  }

  // Sets the current company's distributor to the distributor account with a matching distributorAccountId.
  function getDistributorById(distributorAccountId: number | null): DistributorAccount | undefined {
    return distributors.find((distributor) => distributor.distributorAccountId === distributorAccountId);
  }

  // Sets the current company's distributor to the distributor account with a matching distributorAccountId.
  function getDistributorByName(distributorAccountName: string): DistributorAccount | undefined {
    return distributors.find((distributor) => distributor.name.toLowerCase() === distributorAccountName.toLowerCase());
  }

  return failedToLoad ? (
    <Error500Page />
  ) : (
    <div className="container mt-5 w-75">
      <Spinner loading={loading} />
      <datalist id="datalist-options" data-test="distributor-search-datalist">
        {distributors.map((distributor) => (
          <option
            key={distributor.distributorAccountId}
            value={distributor.name}
            data-test="distributor-search-data-option"
          />
        ))}
      </datalist>
      <form data-test="company-filter-form">
        <input
          type="search"
          className={`${styles.companyFilter} form-control-lg w-100`}
          id="companyDistributorSearchInput"
          placeholder="Filter results..."
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
          data-test="company-filter-input"
        />
      </form>
      <div data-test="company-list-container" className={`${styles.companyList} container g-2 mt-1`}>
        <ul>
          {filteredCompanies.map((company) => (
            <CompanyListItem
              key={company.companyId}
              companyId={company.companyId}
              companyName={company.name}
              distributorAccountId={company.distributorAccountId}
              errorMessage={errorMessage}
              isActive={company.companyId === openCompanyId}
              isValidDistributorName={isValidDistributorName}
              onToggle={(companyId) => toggleOpenCompany(companyId)}
              getDistributorById={(distributorAccountId) => getDistributorById(distributorAccountId)}
              getDistributorByName={(distributorAccountName) => getDistributorByName(distributorAccountName)}
              onChangeDistributorAccountId={(filter) => checkCompanyDistributorLink(filter)}
              onLinkDistributorAccount={(distributorAccountId) =>
                linkDistributorAccount(company.companyId, distributorAccountId)
              }
              onDeleteLink={() => deleteCompanyDistributorLink(company.companyId)}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

interface ResponseBody {
  companies: Company[];
  distributors: DistributorAccount[];
}

interface Company {
  companyId: number;
  name: string;
  distributorAccountId: number | null;
}

interface DistributorAccount {
  distributorAccountId: number;
  name: string;
}

interface DistributorLinkResponseBody {
  message: string;
}
